/**
 * tee-upload-progress-monitor.js
 * TeeInBlue Upload Progress Monitor - JavaScript extracted from tee-upload-progress-monitor.liquid
 * Note: Translations are passed via window.teeUploadMessages config
 */

(function() {
  'use strict';

  const DEBUG = false;
  const CHECK_DEBOUNCE = 50;
  const MESSAGE_TRANSITION = 200;

  // Default messages (can be overridden via window.teeUploadMessages)
  const DEFAULT_MESSAGES = [
    { threshold: 0, text: 'Preparing your design...', icon: '📋' },
    { threshold: 25, text: 'Uploading your photo...', icon: '📤' },
    { threshold: 50, text: 'Processing...', icon: '⚙️' },
    { threshold: 75, text: 'Final steps...', icon: '✨' },
    { threshold: 90, text: 'Almost done...', icon: '🚀' }
  ];

  function getMessages() {
    return window.teeUploadMessages || DEFAULT_MESSAGES;
  }

  function shouldInitialize() {
    return document.querySelector('[id^="tee-"], .tee-field, [class*="teeinblue"]') !== null;
  }

  function log(...args) {
    if (DEBUG) console.log('[TeeInBlue Monitor]', ...args);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (shouldInitialize()) initUploadMonitor();
    });
  } else if (shouldInitialize()) {
    initUploadMonitor();
  }

  function initUploadMonitor() {
    log('Initializing upload monitor...');

    const activeUploads = new Map();

    function handleUploadProgress(uploadContainer) {
      if (activeUploads.has(uploadContainer)) return;

      log('New upload detected, setting up monitor...');

      const targetContainer = uploadContainer.closest('.tee-photo-label') || uploadContainer;

      if (window.getComputedStyle(targetContainer).position === 'static') {
        targetContainer.style.position = 'relative';
      }

      const overlay = createOverlay();
      targetContainer.appendChild(overlay.element);

      const originalProgress = uploadContainer.querySelector('.tee-progress');
      if (originalProgress) originalProgress.style.visibility = 'hidden';

      const tracker = {
        container: uploadContainer,
        overlay: overlay,
        currentMessageIndex: -1,
        rafId: null,
        pendingProgress: null
      };

      activeUploads.set(uploadContainer, tracker);

      const observer = new MutationObserver(() => {
        const progressBar = uploadContainer.querySelector('.tee-progress-bar');
        const progressElement = uploadContainer.querySelector('.tee-progress');

        const isComplete =
          (progressElement && !progressElement.classList.contains('tee-progress--loading')) ||
          !uploadContainer.classList.contains('tee-photo-uploading') ||
          targetContainer.querySelector('.tee-photo-uploaded') !== null ||
          targetContainer.querySelector('.tee-photo-uploaded .tee-photo-image') !== null ||
          !document.body.contains(uploadContainer);

        if (isComplete) {
          log('Upload complete, cleaning up...');
          cleanup(uploadContainer);
          return;
        }

        if (progressBar) {
          const progress = parseInt(progressBar.getAttribute('aria-valuenow')) || 0;
          updateProgress(uploadContainer, progress);
        }
      });

      observer.observe(targetContainer, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true
      });

      tracker.observer = observer;

      const initialBar = uploadContainer.querySelector('.tee-progress-bar');
      if (initialBar) {
        const initialProgress = parseInt(initialBar.getAttribute('aria-valuenow')) || 0;
        updateProgress(uploadContainer, initialProgress);
      }
    }

    function createOverlay() {
      const overlay = document.createElement('div');
      overlay.className = 'custom-upload-overlay';

      const messageContainer = document.createElement('div');
      messageContainer.className = 'upload-message-container';

      const icon = document.createElement('span');
      icon.className = 'upload-icon';

      const text = document.createElement('span');
      text.className = 'upload-text';

      const dots = document.createElement('span');
      dots.className = 'progress-dots';
      dots.innerHTML = '<span>.</span><span>.</span><span>.</span>';

      messageContainer.appendChild(icon);
      messageContainer.appendChild(text);
      messageContainer.appendChild(dots);

      const progressBar = document.createElement('div');
      progressBar.className = 'custom-progress-bar';
      progressBar.style.width = '0%';

      overlay.appendChild(messageContainer);
      overlay.appendChild(progressBar);

      return {
        element: overlay,
        messageContainer,
        icon,
        text,
        progressBar
      };
    }

    function updateProgress(uploadContainer, progress) {
      const tracker = activeUploads.get(uploadContainer);
      if (!tracker) return;

      tracker.pendingProgress = progress;

      if (tracker.rafId !== null) return;

      tracker.rafId = requestAnimationFrame(() => {
        tracker.rafId = null;
        const prog = tracker.pendingProgress;
        const MESSAGES = getMessages();

        const messageIndex = getMessageIndex(prog, MESSAGES);
        if (messageIndex !== tracker.currentMessageIndex) {
          updateMessage(tracker, messageIndex, MESSAGES);
        }

        tracker.overlay.progressBar.style.width = prog + '%';
      });
    }

    function getMessageIndex(progress, MESSAGES) {
      for (let i = MESSAGES.length - 1; i >= 0; i--) {
        if (progress >= MESSAGES[i].threshold) return i;
      }
      return 0;
    }

    function updateMessage(tracker, messageIndex, MESSAGES) {
      tracker.currentMessageIndex = messageIndex;
      const { messageContainer, icon, text } = tracker.overlay;
      const message = MESSAGES[messageIndex];

      messageContainer.style.opacity = '0';
      messageContainer.style.transform = 'scale(0.9)';
      messageContainer.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';

      setTimeout(() => {
        icon.textContent = message.icon;
        text.textContent = message.text;

        icon.style.animation = 'none';
        text.style.animation = 'none';

        requestAnimationFrame(() => {
          messageContainer.style.opacity = '1';
          messageContainer.style.transform = 'scale(1)';
          messageContainer.style.transition = 'opacity 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)';

          icon.style.animation = 'slideInLeft 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
          text.style.animation = 'slideInRight 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s';
        });
      }, MESSAGE_TRANSITION);
    }

    function cleanup(uploadContainer) {
      const tracker = activeUploads.get(uploadContainer);
      if (!tracker) return;

      if (tracker.rafId !== null) {
        cancelAnimationFrame(tracker.rafId);
      }

      if (tracker.observer) {
        tracker.observer.disconnect();
      }

      if (tracker.overlay.element.parentNode) {
        tracker.overlay.element.remove();
      }

      const originalProgress = uploadContainer.querySelector('.tee-progress');
      if (originalProgress) {
        originalProgress.style.visibility = '';
      }

      uploadContainer.removeAttribute('data-progress-monitored');
      activeUploads.delete(uploadContainer);

      log('Cleanup complete');
    }

    let checkTimeout = null;
    function checkForUploadContainers() {
      if (checkTimeout) return;

      checkTimeout = setTimeout(() => {
        checkTimeout = null;
        const containers = document.querySelectorAll('.tee-photo-uploading:not([data-progress-monitored])');

        containers.forEach(container => {
          container.setAttribute('data-progress-monitored', 'true');
          handleUploadProgress(container);
        });
      }, CHECK_DEBOUNCE);
    }

    const eventOptions = { passive: true };

    document.addEventListener('teeinblue-event-page-changed', (e) => {
      log('Page changed', e.detail);
      checkForUploadContainers();
    }, eventOptions);

    document.addEventListener('teeinblue-event-component-injected', (e) => {
      log('Component injected', e.detail);
      checkForUploadContainers();
    }, eventOptions);

    const domObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === 1 &&
                (node.classList?.contains('tee-photo-uploading') ||
                 node.querySelector?.('.tee-photo-uploading'))) {
              log('Upload container detected via DOM observer');
              checkForUploadContainers();
              return;
            }
          }
        }
      }
    });

    domObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    log('Upload monitor initialized');
  }
})();
